import type { DriveFileSelection } from '../drive/types';

/**
 * The open document's crash cache: what an office app (Write, Calc) keeps on
 * this device while somebody works, so a reload, a crash or leaving the app
 * and coming back brings the same document back — content, file name and
 * Drive binding included.
 *
 * It is not a list of saved files. Each app decides on its own what "saved"
 * means (Drive, a directory of its own); this only remembers the document that
 * was open last, per account. Bytes are stored exactly as the app hands them
 * over, so a password-protected document is cached encrypted, never as
 * plaintext.
 *
 * One IndexedDB database for every app (`nx-documents`), one record per open
 * document, an index per app and account naming the active one. Writes are a
 * compare-and-swap on the record's revision: a second tab that still holds an
 * older revision gets `DocumentCacheConflictError` instead of overwriting the
 * newer state. An unreadable index or record is never dropped silently —
 * `restore` reports it with the raw bytes (`DocumentCacheCorruptError`) and
 * `recover` moves it to a recovery key before the app starts fresh.
 */

const DB = 'nx-documents';
const STORE = 'cache';
const MAX_BYTES = 64 * 1024 * 1024;
const DEFAULT_MAX_ENTRIES = 5;

export interface DocumentPrincipal {
  readonly email: string;
  readonly uuid?: string;
}

/**
 * The owner key every account-scoped document store uses: the account uuid
 * when the shell knows one, else the lower-cased email. A browser session that
 * switches accounts therefore never shows one account's documents to another.
 */
export function documentOwnerKey(principal: DocumentPrincipal | null): string {
  const uuid = principal?.uuid?.trim();
  if (uuid) return `account:uuid:${uuid}`;
  const email = principal?.email.trim().toLowerCase();
  return email ? `account:email:${email}` : 'account:anonymous';
}

export interface CachedDocumentDraft<M = Record<string, unknown>> {
  /** The open document's slot; stays the same while the document is replaced in place. */
  readonly id: string;
  /** What the app shows as the document's name (usually the file name). */
  readonly name: string;
  /** The app's format id for `bytes` (`calcz`, `xlsx`, `writez` …). */
  readonly format: string;
  /** The document as the app persists it — encrypted when the document is protected. */
  readonly bytes: Uint8Array;
  /** The Drive file the document is bound to, including the ETag of the last transfer. */
  readonly drive?: DriveFileSelection;
  /** App-specific state that has to come back with the document (structured-cloneable). */
  readonly meta?: M;
}

export interface CachedDocument<M = Record<string, unknown>> extends CachedDocumentDraft<M> {
  readonly revision: number;
  readonly updatedAt: number;
}

interface CacheIndex {
  activeId?: string;
  entries: { id: string; updatedAt: number }[];
}

export class DocumentCacheConflictError extends Error {
  constructor() {
    super('The document was changed in another tab');
    this.name = 'DocumentCacheConflictError';
  }
}

/**
 * Exactly what `recover` moves aside: the unreadable index, or one document
 * slot. `revision` names the readable revision the app failed to open; without
 * it the slot is the record `restore` found unreadable.
 */
export type DocumentCacheRecoveryTarget =
  | { readonly kind: 'index' }
  | { readonly kind: 'document'; readonly id: string; readonly revision?: number };

/** The cached document (or its index) cannot be read. `raw` holds what was stored, for a download. */
export class DocumentCacheCorruptError extends Error {
  constructor(
    readonly raw: Uint8Array,
    /** What to hand `recover` so it moves exactly this record aside. */
    readonly target: DocumentCacheRecoveryTarget = { kind: 'index' },
  ) {
    super('The cached document cannot be read');
    this.name = 'DocumentCacheCorruptError';
  }
}

export interface DocumentCache<M = Record<string, unknown>> {
  /** The document that was open last, or `undefined`. Throws `DocumentCacheCorruptError`. */
  restore(owner: string): Promise<CachedDocument<M> | undefined>;
  /**
   * Store the open document and make it the active one. `expectedRevision` is
   * the revision this tab last read or wrote for `draft.id` (0 for a new slot).
   */
  save(owner: string, draft: CachedDocumentDraft<M>, expectedRevision: number): Promise<CachedDocument<M>>;
  /**
   * Move exactly `target` — the document (at the revision) `restore` returned
   * and the app could not open, or what `restore` threw as unreadable — to a
   * recovery key, clear it, and return its bytes so the app can offer them as
   * a download. Compare and move are one transaction: when another tab has
   * replaced that record (or the index) since, nothing is touched and the
   * result is `undefined`, so a healthy document is never moved aside.
   */
  recover(owner: string, target: DocumentCacheRecoveryTarget): Promise<Uint8Array | undefined>;
}

export interface DocumentCacheOptions {
  /**
   * Open documents kept per account (other tabs' slots). The slot being saved
   * is always kept; the oldest other slots go first.
   */
  readonly maxEntries?: number;
}

function guarded(store: IDBObjectStore, callback: () => void): () => void {
  return () => {
    try {
      callback();
    } catch {
      store.transaction.abort();
    }
  };
}

function indexValue(value: unknown): CacheIndex {
  if (value == null) return { entries: [] };
  const index = value as CacheIndex;
  if (
    !Array.isArray(index.entries) ||
    index.entries.some((entry) => !entry || typeof entry.id !== 'string') ||
    (index.activeId !== undefined && typeof index.activeId !== 'string')
  )
    throw new Error('Invalid document cache index');
  return index;
}

/** Realm-independent: bytes structured-cloned out of IndexedDB may come from another realm's `Uint8Array`. */
function isBytes(value: unknown): value is Uint8Array {
  return ArrayBuffer.isView(value) && Object.prototype.toString.call(value) === '[object Uint8Array]';
}

function recordLooksValid(value: unknown): value is CachedDocument<unknown> {
  const record = value as CachedDocument<unknown> | undefined;
  return (
    !!record &&
    isBytes(record.bytes) &&
    Number.isSafeInteger(record.revision) &&
    typeof record.id === 'string' &&
    typeof record.name === 'string' &&
    typeof record.format === 'string'
  );
}

/** Whatever a row holds, as bytes for a download. */
export function encodeStored(value: unknown): Uint8Array {
  if (isBytes(value)) return value.slice();
  if (value instanceof ArrayBuffer) return new Uint8Array(value.slice(0));
  if (ArrayBuffer.isView(value))
    return new Uint8Array(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength));
  try {
    return new TextEncoder().encode(JSON.stringify(value));
  } catch {
    return new TextEncoder().encode(String(value));
  }
}

function slotId(id: string): string {
  if (!/^[a-zA-Z0-9_-]{1,100}$/.test(id)) throw new Error('Invalid document identity');
  return id;
}

async function transaction<T>(
  run: (store: IDBObjectStore, finish: (value: T) => void, fail: (error: Error) => void) => void,
): Promise<T> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Cannot open the document cache'));
  });
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      let value: T;
      let failure: Error | undefined;
      tx.oncomplete = () => resolve(value);
      tx.onabort = () => reject(failure ?? tx.error ?? new Error('Document cache transaction aborted'));
      tx.onerror = () => reject(failure ?? tx.error ?? new Error('Document cache transaction failed'));
      try {
        run(
          tx.objectStore(STORE),
          (result) => {
            value = result;
          },
          (error) => {
            failure = error;
            tx.abort();
          },
        );
      } catch (error) {
        failure = error as Error;
        tx.abort();
      }
    });
  } finally {
    db.close();
  }
}

/** The crash cache of one app (`write`, `calc` …). */
export function createDocumentCache<M = Record<string, unknown>>(
  app: string,
  options: DocumentCacheOptions = {},
): DocumentCache<M> {
  if (!/^[a-z][a-z0-9-]*$/.test(app)) throw new Error('Invalid app key');
  const maxEntries = Math.max(1, options.maxEntries ?? DEFAULT_MAX_ENTRIES);
  const key = (owner: string, suffix: string): string => {
    if (!owner) throw new Error('An account key is required');
    return `${owner}:${app}:${suffix}`;
  };
  const indexKey = (owner: string) => key(owner, 'index');
  const recordKey = (owner: string, id: string) => key(owner, `document:${slotId(id)}`);

  function restore(owner: string): Promise<CachedDocument<M> | undefined> {
    return transaction((store, finish, fail) => {
      const req = store.get(indexKey(owner));
      req.onsuccess = guarded(store, () => {
        let index: CacheIndex;
        try {
          index = indexValue(req.result);
        } catch {
          fail(new DocumentCacheCorruptError(encodeStored(req.result), { kind: 'index' }));
          return;
        }
        if (!index.activeId) {
          finish(undefined);
          return;
        }
        const activeId = index.activeId;
        const rec = store.get(recordKey(owner, activeId));
        rec.onsuccess = guarded(store, () => {
          if (rec.result === undefined) finish(undefined);
          else if (!recordLooksValid(rec.result))
            fail(new DocumentCacheCorruptError(encodeStored(rec.result), { kind: 'document', id: activeId }));
          else finish(rec.result as CachedDocument<M>);
        });
      });
    });
  }

  function save(owner: string, draft: CachedDocumentDraft<M>, expectedRevision: number): Promise<CachedDocument<M>> {
    if (draft.bytes.byteLength > MAX_BYTES) return Promise.reject(new Error('Document exceeds 64 MiB'));
    const bytes = draft.bytes.slice();
    return transaction((store, finish, fail) => {
      const address = recordKey(owner, draft.id);
      const req = store.get(address);
      req.onsuccess = guarded(store, () => {
        // An unreadable record is kept for recovery, never overwritten here.
        if (req.result !== undefined && !recordLooksValid(req.result)) {
          fail(new DocumentCacheCorruptError(encodeStored(req.result), { kind: 'document', id: draft.id }));
          return;
        }
        const previous = req.result as CachedDocument<M> | undefined;
        if ((previous?.revision ?? 0) !== expectedRevision) {
          fail(new DocumentCacheConflictError());
          return;
        }
        const saved: CachedDocument<M> = {
          id: draft.id,
          name: draft.name.trim().slice(0, 200),
          format: draft.format,
          bytes,
          ...(draft.drive ? { drive: { ...draft.drive } } : {}),
          ...(draft.meta !== undefined ? { meta: draft.meta } : {}),
          revision: expectedRevision + 1,
          updatedAt: Date.now(),
        };
        store.put(saved, address);
        const idx = store.get(indexKey(owner));
        idx.onsuccess = guarded(store, () => {
          // An unreadable index is kept for recovery, never overwritten here.
          const index = indexValue(idx.result);
          // The slot just saved (the active one from now on) is never evicted,
          // whatever the clock says — only other slots, oldest first.
          const others = index.entries
            .filter((entry) => entry.id !== saved.id)
            .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
          const kept = [{ id: saved.id, updatedAt: saved.updatedAt }, ...others.slice(0, maxEntries - 1)];
          for (const stale of others.slice(maxEntries - 1)) store.delete(recordKey(owner, stale.id));
          store.put({ activeId: saved.id, entries: kept } satisfies CacheIndex, indexKey(owner));
          finish(saved);
        });
      });
    });
  }

  function recover(owner: string, target: DocumentCacheRecoveryTarget): Promise<Uint8Array | undefined> {
    return transaction((store, finish) => {
      const address = indexKey(owner);
      const stamp = Date.now();
      const req = store.get(address);
      req.onsuccess = guarded(store, () => {
        let index: CacheIndex | undefined;
        try {
          index = indexValue(req.result);
        } catch {
          index = undefined;
        }
        if (target.kind === 'index') {
          // Only an index that is still unreadable moves aside.
          if (index) {
            finish(undefined);
            return;
          }
          store.put(req.result, `${address}:recovery:${stamp}`);
          store.put({ entries: [] } satisfies CacheIndex, address);
          finish(encodeStored(req.result));
          return;
        }
        const id = target.id;
        const recAddress = recordKey(owner, id);
        const rec = store.get(recAddress);
        rec.onsuccess = guarded(store, () => {
          const value: unknown = rec.result;
          const readable = recordLooksValid(value);
          // The compare: still the unreadable record, or still the revision the
          // app failed to open. Anything else is another tab's newer state.
          const same =
            value !== undefined &&
            (target.revision === undefined ? !readable : readable && value.revision === target.revision);
          if (!same) {
            finish(undefined);
            return;
          }
          store.put(value, `${recAddress}:recovery:${stamp}`);
          store.delete(recAddress);
          if (index) {
            const entries = index.entries.filter((entry) => entry.id !== id);
            const activeId = index.activeId === id ? undefined : index.activeId;
            store.put((activeId ? { activeId, entries } : { entries }) satisfies CacheIndex, address);
          }
          finish(readable ? value.bytes.slice() : encodeStored(value));
        });
      });
    });
  }

  return { restore, save, recover };
}
